/**
 * Migration 002 — note string → Y.Doc + docHtml.
 *
 * Legacy tasks (created via email-to-totodo, v1 import, or old API) store
 * content in the plain-text `note` field. v2 uses Y.Doc for rich text.
 *
 * This migration converts `note` → Y.Doc (stored in $totodo.doc_updates)
 * and sets `docHtml` for search/preview.
 *
 * After successful conversion the `note` field is cleared so the migration
 * is idempotent and won't re-process the same task.
 *
 * Uses `orderBy("note")` which is index-based — Dexie only returns rows
 * that actually have a note value, making this the fastest query approach
 * (per Dexie author David Fahlander).
 *
 * Requires DocContext — skips silently if not yet configured (retry next startup).
 */

import { db } from "@/db/schema"
import { hasDocContext, setTaskDoc } from "@/db/operations"
import type { DataMigration } from "./migrationTypes"

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const BATCH_SIZE = 50 // Smaller than 001 — setTaskDoc is heavier (Y.Doc creation)
const YIELD_MS = 100

// ---------------------------------------------------------------------------
// Migration
// ---------------------------------------------------------------------------

export const noteToDocMigration: DataMigration = {
  id: "002-note-to-doc",
  description:
    "Convert legacy note field to Y.Doc rich text (email-to-totodo, imports)",
  priority: 6, // After userStatus (5), but still important

  async needsMigration(): Promise<boolean> {
    if (!hasDocContext()) return false // DocContext not ready — retry next startup

    // orderBy("note") uses the index — only returns rows that have a note value.
    // Much faster than .filter() which scans all rows.
    const sample = await db.totodo
      .orderBy("note")
      .limit(5)
      .toArray()

    return sample.some(
      (obj) => !!obj.note && obj.note.trim().length > 0,
    )
  },

  async execute(): Promise<number> {
    if (!hasDocContext()) return 0

    let totalMigrated = 0

    // eslint-disable-next-line no-constant-condition
    while (true) {
      // orderBy("note") is index-based — only returns rows with a note value
      const batch = await db.totodo
        .orderBy("note")
        .limit(BATCH_SIZE)
        .toArray()

      // Filter out empty/whitespace-only notes
      const toMigrate = batch.filter(
        (obj) => !!obj.note && obj.note.trim().length > 0,
      )

      if (toMigrate.length === 0) break

      for (const obj of toMigrate) {
        try {
          const result = await setTaskDoc(obj.id!, obj.note!)
          if (result.ok) {
            // Clear note after successful conversion — removes from index
            await db.totodo.update(obj.id!, { note: "" })
            totalMigrated++
          }
        } catch (e) {
          console.warn(
            `[Migration 002] Failed to convert note for ${obj.id}:`,
            e,
          )
          // Continue with next — don't block the batch
        }
      }

      // Yield to main thread between batches
      if (toMigrate.length === BATCH_SIZE) {
        await new Promise((resolve) => setTimeout(resolve, YIELD_MS))
      } else {
        break
      }
    }

    return totalMigrated
  },
}
